
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { TaskChangeRequest } from "../../types/tasks";
import { formatDistanceToNow } from "date-fns";
import { AlertCircle, Calendar, CheckCircle, Users, XCircle } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface TaskChangeRequestsProps {
  requests: TaskChangeRequest[];
  onRespond?: (requestId: string, requestType: string, approve: boolean) => void;
}

const TaskChangeRequests = ({ requests, onRespond }: TaskChangeRequestsProps) => {
  // State for modals
  const [selectedRequest, setSelectedRequest] = useState<TaskChangeRequest | null>(null);
  const [showReassignDialog, setShowReassignDialog] = useState(false);
  const [showExtendDialog, setShowExtendDialog] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);

  // Mock user list - in a real app, this would come from an API
  const mockUsers = [
    { id: "user1", name: "Alice Johnson" },
    { id: "user2", name: "Bob Smith" },
    { id: "user3", name: "Carol Davis" },
    { id: "user4", name: "Dave Wilson" },
  ];
  
  // Filter to only show pending requests first, then most recent
  const sortedRequests = [...requests].sort((a, b) => {
    // First sort by status (pending first)
    if (a.status === "pending" && b.status !== "pending") return -1;
    if (a.status !== "pending" && b.status === "pending") return 1;
    
    // Then sort by date (most recent first)
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
  
  if (requests.length === 0) {
    return null;
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "approved":
        return (
          <Badge className="bg-green-100 text-green-800 border-green-200 flex items-center gap-1">
            <CheckCircle className="h-3 w-3" /> Approved
          </Badge>
        );
      case "rejected":
        return (
          <Badge className="bg-red-100 text-red-800 border-red-200 flex items-center gap-1">
            <XCircle className="h-3 w-3" /> Rejected
          </Badge>
        );
      case "pending":
      default:
        return (
          <Badge className="bg-amber-100 text-amber-800 border-amber-200 flex items-center gap-1">
            <AlertCircle className="h-3 w-3" /> Pending
          </Badge>
        );
    }
  };

  const getRequestTypeLabel = (type: string) => {
    return type === "reassign" ? "Reassignment" : "Deadline Extension";
  };
  
  const handleApproveClick = (request: TaskChangeRequest) => {
    setSelectedRequest(request);
    if (request.requestType === "reassign") {
      setShowReassignDialog(true);
    } else if (request.requestType === "extend") {
      setShowExtendDialog(true);
    }
  };
  
  const handleRejectClick = (request: TaskChangeRequest) => {
    if (onRespond) {
      onRespond(request.id, request.requestType, false);
    }
  };
  
  const completeReassignApproval = () => {
    if (selectedRequest && selectedUserId && onRespond) {
      onRespond(selectedRequest.id, selectedRequest.requestType, true);
      setShowReassignDialog(false);
      setSelectedRequest(null);
      setSelectedUserId("");
    }
  };
  
  const completeExtendApproval = () => {
    if (selectedRequest && selectedDate && onRespond) {
      onRespond(selectedRequest.id, selectedRequest.requestType, true);
      setShowExtendDialog(false);
      setSelectedRequest(null);
      setSelectedDate(undefined);
    }
  };

  return (
    <div className="border-t pt-4">
      <h4 className="text-sm font-medium mb-2">Change Requests</h4>
      <div className="space-y-3">
        {sortedRequests.map(request => (
          <div 
            key={request.id} 
            className={`
              p-3 rounded-md 
              ${request.status === "pending" 
                ? "bg-amber-50 border border-amber-200" 
                : request.status === "approved" 
                  ? "bg-green-50 border border-green-200" 
                  : "bg-red-50 border border-red-200"
              }
            `}
          >
            <div className="flex justify-between">
              <span className="text-sm font-medium">
                {getRequestTypeLabel(request.requestType)} Request
              </span>
              {getStatusBadge(request.status)}
            </div>
            <p className="text-sm mt-1">{request.details}</p>
            <div className="flex justify-between items-center mt-2">
              <p className="text-xs text-muted-foreground">
                {formatDistanceToNow(new Date(request.createdAt), { addSuffix: true })}
              </p>
              
              {request.status === "pending" && onRespond && (
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="border-green-200 bg-green-50 text-green-700 hover:bg-green-100 hover:text-green-800"
                    onClick={() => handleApproveClick(request)}
                  >
                    <CheckCircle className="h-4 w-4 mr-1" /> 
                    {request.requestType === "reassign" ? "Reassign" : "Extend Deadline"}
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="border-red-200 bg-red-50 text-red-700 hover:bg-red-100 hover:text-red-800"
                    onClick={() => handleRejectClick(request)}
                  >
                    <XCircle className="h-4 w-4 mr-1" /> Reject
                  </Button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Reassignment Dialog */}
      <Dialog open={showReassignDialog} onOpenChange={setShowReassignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reassign Task</DialogTitle>
            <DialogDescription>
              Select a new person to assign this task to.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <label className="text-sm font-medium mb-2 block">
              New Assignee
            </label>
            <Select value={selectedUserId} onValueChange={setSelectedUserId}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a person" />
              </SelectTrigger>
              <SelectContent>
                {mockUsers.map(user => (
                  <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReassignDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={completeReassignApproval}
              disabled={!selectedUserId}
              className="flex items-center gap-2"
            >
              <Users className="h-4 w-4" /> Complete Reassignment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deadline Extension Dialog */}
      <Dialog open={showExtendDialog} onOpenChange={setShowExtendDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Extend Deadline</DialogTitle>
            <DialogDescription>
              Select a new deadline for this task.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <label className="text-sm font-medium mb-2 block">
              New Deadline
            </label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant={"outline"}
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !selectedDate && "text-muted-foreground"
                  )}
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  {selectedDate ? format(selectedDate, "PPP") : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  initialFocus
                  disabled={(date) => date < new Date()}
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExtendDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={completeExtendApproval}
              disabled={!selectedDate}
              className="flex items-center gap-2"
            >
              <Calendar className="h-4 w-4" /> Set New Deadline
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TaskChangeRequests;
